import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello, I'm here on Anant's behalf. What's alive for you today?" },
  ]);
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`sess_${Math.random().toString(36).slice(2, 10)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/ask-anant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + evt.delta };
                return copy;
              });
            }
          } catch (parseErr) {
            console.warn("chat SSE parse skipped:", parseErr, line);
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I couldn't reach that space right now. Please try again in a moment." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="chatbot-open-btn"
        className="fixed bottom-6 right-6 z-50 bg-primary-ub text-white rounded-full pl-4 pr-5 py-3 shadow-lg hover:scale-105 transition-transform flex items-center gap-2"
      >
        <Sparkles size={18} />
        <span className="font-medium">Ask Anant</span>
      </button>
    );
  }

  return (
    <div
      data-testid="chatbot-panel"
      className="fixed bottom-6 right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 max-h-[70vh] bg-ub border border-ub rounded-3xl shadow-2xl flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-ub bg-ub-alt">
        <div>
          <div className="font-serif text-xl">Ask Anant</div>
          <div className="text-xs text-muted-ub">A gentle digital companion</div>
        </div>
        <button onClick={() => setOpen(false)} data-testid="chatbot-close-btn" className="p-1.5 rounded-full hover:bg-ub">
          <X size={18} />
        </button>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.map((m, i) => (
          <div key={i} data-testid={`chat-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user" ? "bg-primary-ub text-white" : "bg-ub-alt text-ub"
              }`}
            >
              {m.content || <span className="opacity-60">…</span>}
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-ub flex items-center gap-2 bg-ub">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="What's on your heart?"
          data-testid="chatbot-input"
          className="flex-1 px-4 py-2.5 rounded-full bg-ub-alt border border-ub outline-none focus:border-[var(--ub-primary)] text-sm"
        />
        <button
          onClick={send}
          disabled={busy}
          data-testid="chatbot-send-btn"
          className="p-2.5 rounded-full bg-primary-ub text-white hover:bg-[var(--ub-primary-hover)] transition-colors disabled:opacity-50"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
